import { useEffect, useId, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ChevronRightRegular,
  ChevronDownRegular,
} from '@fluentui/react-icons';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { StageEventSnapshot, PipelineStageId, StageStepId } from '@/types/agui';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  stageRow: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: tokens.borderRadiusSmall,
    overflow: 'hidden',
  },
  stageRowActive: {
    borderLeft: `2px solid ${tokens.colorBrandStroke1}`,
    paddingLeft: tokens.spacingHorizontalXS,
  },
  stageRowFailed: {
    borderLeft: `2px solid ${tokens.colorPaletteRedBorder1}`,
    paddingLeft: tokens.spacingHorizontalXS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    width: '100%',
    textAlign: 'left',
    color: 'inherit',
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
  },
  headerClickable: {
    cursor: 'pointer',
  },
  chevronWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  emoji: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    flexShrink: 0,
  },
  summary: {
    flexGrow: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandStroke1,
    flexShrink: 0,
    animationName: {
      from: { opacity: 0.35 },
      to: { opacity: 1 },
    },
    // Use the motion-token "slower" duration (~500ms) doubled by alternate
    // direction → ~1s round trip — close to the prior 1.2s but driven by a
    // design-system token rather than a hard-coded value.
    animationDuration: tokens.durationUltraSlow,
    animationIterationCount: 'infinite',
    animationDirection: 'alternate',
    animationTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      // Honor the user's OS-level preference: hold a stable dim opacity
      // (still visible as "in flight") instead of pulsing.
      animationIterationCount: 0,
      animationName: 'none',
      opacity: 0.7,
    },
  },
  body: {
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
});

interface TurnStagesPanelProps {
  /** Stages to render, in execution order. */
  stages: StageEventSnapshot[];
  /**
   * When true the panel is treated as live (the most-recent stage's expansion
   * tracks the active stage). When false (rehydrated turns) every stage
   * starts collapsed and the user expands what they want to inspect.
   */
  live: boolean;
}

/**
 * One stage card per row, rendered as a collapsible disclosure. The active
 * (in-flight) stage auto-expands so the user sees live progress; completed
 * stages collapse to a single-line summary the user can expand on demand.
 *
 * Once the user manually toggles a stage's expansion, the auto-collapse
 * behavior steps out of the way for that stage — i.e. if you open a completed
 * Plan card to inspect it, subsequent live updates to other stages will not
 * close it on you.
 *
 * Rehydrated stages (from chat history) all start collapsed.
 */
export const TurnStagesPanel = ({ stages, live }: TurnStagesPanelProps) => {
  const styles = useStyles();
  // Per-panel id prefix so the disclosure aria-controls/id pairs are unique
  // even when multiple TurnStagesPanel instances exist on screen at once
  // (one per assistant turn). Without this, screen readers would see
  // duplicate ids like "stage-body-plan" tied to every assistant message.
  const panelId = useId();

  // Determine which stage is active (the most-recent non-terminal one).
  const activeIndex = useMemo(() => {
    if (!live || stages.length === 0) return -1;
    for (let i = stages.length - 1; i >= 0; i--) {
      const step = stages[i]?.step;
      if (step === 'started' || step === 'progress') return i;
    }
    return -1;
  }, [stages, live]);

  // Auto-expanded set (managed by the component) and manually-toggled set
  // (managed by the user). User toggles take precedence over auto-collapse so
  // a stage the user explicitly opened won't close on the next live update.
  const [autoExpanded, setAutoExpanded] = useState<Set<string>>(new Set());
  const [manuallyToggled, setManuallyToggled] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    if (!live) return;
    setAutoExpanded((prev) => {
      const next = new Set(prev);
      stages.forEach((s, i) => {
        if (i === activeIndex) {
          next.add(s.stage);
        } else if (s.step === 'completed' || s.step === 'failed' || s.step === 'skipped') {
          next.delete(s.stage);
        }
      });
      return next;
    });
  }, [stages, activeIndex, live]);

  if (stages.length === 0) {
    return null;
  }

  const isExpanded = (stage: PipelineStageId): boolean => {
    // User override wins; otherwise fall back to the auto-expand state.
    const manual = manuallyToggled.get(stage);
    if (manual !== undefined) return manual;
    return autoExpanded.has(stage);
  };

  const toggle = (stage: PipelineStageId) => {
    const current = isExpanded(stage);
    setManuallyToggled((prev) => {
      const next = new Map(prev);
      next.set(stage, !current);
      return next;
    });
  };

  return (
    <div className={styles.container} role="list" aria-label="Pipeline stages">
      {stages.map((s, i) => {
        const expanded = isExpanded(s.stage);
        const isActive = i === activeIndex;
        const isFailed = s.step === 'failed';
        const rowClass = mergeClasses(
          styles.stageRow,
          isActive && styles.stageRowActive,
          isFailed && styles.stageRowFailed,
        );
        const hasDetails = !!s.details;
        return (
          <div key={s.stage} className={rowClass} role="listitem">
            <div
              id={`${panelId}-${s.stage}-header`}
              className={mergeClasses(styles.header, hasDetails && styles.headerClickable)}
              {...(hasDetails ? {
                role: 'button',
                tabIndex: 0,
                onClick: () => toggle(s.stage),
                onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(s.stage); } },
                'aria-expanded': expanded,
                'aria-controls': `${panelId}-${s.stage}-body`,
              } : {})}
            >
              {hasDetails && (
                <span aria-hidden="true" className={styles.chevronWrapper}>
                  {expanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                </span>
              )}
              {s.emoji && <span className={styles.emoji} aria-hidden="true">{s.emoji}</span>}
              <Text size={200} className={styles.summary}>
                {s.summary || stageDefaultLabel(s.stage)}
              </Text>
              {isActive && <span className={styles.dot} aria-label="Active" />}
              {!isActive && <StageStepBadge step={s.step} />}
            </div>
            {expanded && hasDetails && (
              <div
                className={styles.body}
                id={`${panelId}-${s.stage}-body`}
                role="region"
                aria-labelledby={`${panelId}-${s.stage}-header`}
              >
                <Markdown remarkPlugins={[remarkGfm]}>{s.details}</Markdown>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

function stageDefaultLabel(stage: PipelineStageId): string {
  switch (stage) {
    case 'plan':     return 'Plan';
    case 'build':    return 'Build';
    case 'validate': return 'Validate';
    case 'retry':    return 'Retry';
    case 'stream':   return 'Stream';
    default:         return stage;
  }
}

const useBadgeStyles = makeStyles({
  badge: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
});

function StageStepBadge({ step }: { step: StageStepId }) {
  const styles = useBadgeStyles();
  const label = step === 'completed' ? '✓' : step === 'failed' ? '✗' : step === 'skipped' ? '–' : '';
  if (!label) return null;
  return <span className={styles.badge} aria-label={step}>{label}</span>;
}
